import React, { useState, useEffect, useRef } from 'react'
import Amplify, { Auth, Hub } from 'aws-amplify'
import { Switch, Route, useHistory, useLocation } from 'react-router-dom'
import { Userpilot } from 'userpilot'
import { QueryCache, ReactQueryCacheProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query-devtools'

import { Layout, Sidebar, PrivateRoute } from 'components'
import { Loader } from 'components/UI'
import { Explorer, Credentials, Home } from 'pages'

import { awsConfig, config } from 'consts'
import { buildClientIdAllowlist, resolveCognitoClientId } from 'utils/cognitoClientId'
import { attributeMapFromIdTokenPayload } from 'utils/idTokenAttributes'
import 'styles/main.css'

/** Matches expereo.one / Dev Portal V2 local + legacy Hosted UI scopes. */
const OAUTH_SCOPES_WITH_ADMIN = ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin']

/** Matches expereo.one federated (spa-sso-sp) scopes — no Cognito GetUser/admin scope. */
const OAUTH_SCOPES_FEDERATED = ['email', 'profile', 'openid']

const env = process.env.REACT_APP_ENV
const defaultClientId = awsConfig.WEBCLIENT_ID[env]
/** E1 spa-local client — set via REACT_APP_SSO_LOCAL_USERPOOL_CLIENT_ID (.env / GitLab CI). */
const spaLocalClientId = process.env.REACT_APP_SSO_LOCAL_USERPOOL_CLIENT_ID?.trim() || ''
/** E1 spa-sso-sp client — set via REACT_APP_SSO_SP_USERPOOL_CLIENT_ID (.env / GitLab CI). */
const spaSsoClientId = process.env.REACT_APP_SSO_SP_USERPOOL_CLIENT_ID?.trim() || ''

const clientIdAllowlist = buildClientIdAllowlist(defaultClientId, [
  spaLocalClientId,
  spaSsoClientId,
])

const cookieHeader = typeof document === 'undefined' ? '' : document.cookie
const resolvedUserPoolClientId = resolveCognitoClientId({
  defaultClientId,
  allowlist: clientIdAllowlist,
  cookieHeader,
})

const isFederatedClient = spaSsoClientId !== '' && resolvedUserPoolClientId === spaSsoClientId

Amplify.configure({
  Auth: {
    region: 'eu-central-1',
    userPoolId: awsConfig.USERPOOL_ID[env],
    userPoolWebClientId: resolvedUserPoolClientId,
    cookieStorage: awsConfig.COOKIE_STORAGE[env],
  },
})

Auth.configure({
  domain: awsConfig.AUTH_DOMAIN[env],
  scope: isFederatedClient ? OAUTH_SCOPES_FEDERATED : OAUTH_SCOPES_WITH_ADMIN,
  redirectSignIn: awsConfig.REDIRECT_URI[env],
  redirectSignOut: awsConfig.REDIRECT_URI[env],
  responseType: 'code',
})

const queryCache = new QueryCache()

async function attributesFromSession() {
  const session = await Auth.currentSession()
  return attributeMapFromIdTokenPayload(session.getIdToken().payload)
}

/**
 * Federated spa-sso-sp tokens omit aws.cognito.signin.user.admin.
 * Amplify then returns a CognitoUser with no `attributes` — hydrate from the ID token.
 */
async function getAuthenticatedUser() {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: true })
    if (user?.attributes && Object.keys(user.attributes).length > 0) {
      return user
    }
    return {
      ...user,
      attributes: await attributesFromSession(),
    }
  } catch (primaryError) {
    try {
      return {
        attributes: await attributesFromSession(),
      }
    } catch {
      throw primaryError
    }
  }
}

function App() {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const history = useHistory()
  const { pathname } = useLocation()
  const pathnameRef = useRef()

  useEffect(() => {
    Userpilot.initialize(config.USERPILOT_TOKEN[process.env.REACT_APP_ENV])
  }, [])

  useEffect(() => {
    if (pathnameRef.current !== pathname) {
      Userpilot.reload()
    }

    pathnameRef.current = pathname
  }, [pathname])

  useEffect(() => {
    try {
      Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
          case 'signIn':
            console.debug('[ ℹ ] (App.js:43):', data)
            setUser(data)
            break
          case 'signOut':
            Userpilot.anonymous()
            setUser(null)
            break
          default:
            break
        }
      })
    } catch (err) {
      console.error('Hub error :: ', err)
    }
  })

  async function authCheck() {
    try {
      setLoading(true)
      const authenticatedUser = await getAuthenticatedUser()
      const attrs = authenticatedUser?.attributes || {}
      const { email, family_name, given_name, accountName: company } = attrs

      setUser(authenticatedUser)

      try {
        Userpilot.identify(email, {
          name: `${given_name || ''} ${family_name || ''}`.trim(),
          email,
          permissions: attrs['custom:sf_permissions'],
          company,
          created_at: Date.now(), // Signup date as a Unix timestamp
        })
      } catch (err) {
        console.error('Userpilot identify error :: ', err)
      }
    } catch (err) {
      // console.error('currentAuthenticatedUser error :: ', err)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!user) {
      authCheck()
    }
  }, [user])

  const handleLogout = async () => {
    await Auth.signOut()
    history.push('/')
  }

  if (loading) return <Loader className="h-screen">loading...</Loader>

  return (
    <ReactQueryCacheProvider queryCache={queryCache}>
      <Layout className="background">
        <Sidebar user={user} onLogout={handleLogout} />
        <Switch>
          <PrivateRoute path="/apiexplorer" user={user}>
            <Explorer />
          </PrivateRoute>
          <PrivateRoute path="/credentials" user={user}>
            <Credentials />
          </PrivateRoute>
          <Route path="/">
            <Home />
          </Route>
        </Switch>
      </Layout>
      <ReactQueryDevtools />
    </ReactQueryCacheProvider>
  )
}

export default App
